import React, { useState, useRef, useEffect, useCallback } from "react";
import play_next from '../assets/images/AudioTour/play-next (1).svg'
import play_prev from '../assets/images/AudioTour/play-next (1).svg'


const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

// Helper function to format time in MM:SS format
const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const AudioGridSection = ({ fields }) => {
    // All hooks must be called at the top level, before any early returns
    // State for audio playback
    const [playingIndex, setPlayingIndex] = useState(null);
    const [currentTimes, setCurrentTimes] = useState({});
    const [durations, setDurations] = useState({});
    const [progress, setProgress] = useState({});
    const [isDragging, setIsDragging] = useState(null); // Track which audio is being dragged
    const audioRefs = useRef({});
    const dragRef = useRef({ index: null, startX: 0 });

    // Get cards array (handle null/undefined safely)
    const cards = fields && Array.isArray(fields.cards) ? fields.cards : [];

    // Get flag image URL from API response
    const getFlagUrl = (flag) => {
        if (!flag) return null;

        // Try multiple possible URL locations
        const url =
            flag.url ||
            flag.sizes?.["2048x2048"] ||
            flag.sizes?.large ||
            flag.sizes?.["1536x1536"] ||
            flag.sizes?.medium_large ||
            flag.sizes?.medium ||
            null;

        return url;
    };

    // Handle play/pause
    const handlePlayPause = (index, audioUrl) => {
        if (!audioUrl) return;

        const audio = audioRefs.current[index];
        if (!audio) return;

        if (playingIndex === index) {
            // Pause current audio
            audio.pause();
            setPlayingIndex(null);
        } else {
            // Stop any currently playing audio
            if (playingIndex !== null) {
                const prevAudio = audioRefs.current[playingIndex];
                if (prevAudio) {
                    prevAudio.pause();
                    // Don't reset currentTime - allow user to resume from where they left off
                }
            }

            // Play new audio
            audio.play().catch((error) => {
                setPlayingIndex(null);
            });
            setPlayingIndex(index);
        }
    };

    // Update progress and time
    useEffect(() => {
        const updateProgress = () => {
            const newCurrentTimes = {};
            const newProgress = {};

            Object.keys(audioRefs.current).forEach((key) => {
                const audio = audioRefs.current[key];
                if (audio) {
                    const index = parseInt(key);
                    newCurrentTimes[index] = audio.currentTime;

                    if (audio.duration && isFinite(audio.duration)) {
                        newProgress[index] = (audio.currentTime / audio.duration) * 100;
                    }
                }
            });

            setCurrentTimes(newCurrentTimes);
            setProgress(newProgress);
        };

        const interval = setInterval(updateProgress, 100);
        return () => clearInterval(interval);
    }, [playingIndex]);

    // Get audio file URL from API response (moved before useCallback)
    const getAudioUrl = (audioFile) => {
        if (!audioFile) return null;
        return audioFile.url || null;
    };

    // Handle audio loaded metadata
    const handleLoadedMetadata = useCallback((index, audio) => {
        if (audio.duration && isFinite(audio.duration)) {
            setDurations((prev) => ({
                ...prev,
                [index]: audio.duration,
            }));
        }
    }, []);

    // Handle audio end
    const handleEnded = useCallback((index) => {
        setPlayingIndex(null);
        setCurrentTimes((prev) => ({
            ...prev,
            [index]: 0,
        }));
        setProgress((prev) => ({
            ...prev,
            [index]: 0,
        }));
    }, []);

    // Initialize audio elements when cards change
    useEffect(() => {
        if (!cards.length) return;

        // Clean up existing audio elements
        Object.values(audioRefs.current).forEach((audio) => {
            if (audio) {
                audio.pause();
                audio.removeEventListener("loadedmetadata", () => { });
                audio.removeEventListener("ended", () => { });
                audio.src = "";
                audio.load();
            }
        });
        audioRefs.current = {};

        // Initialize new audio elements
        cards.forEach((card, index) => {
            const audioUrl = getAudioUrl(card.audio_file);
            if (audioUrl) {
                const audio = new Audio(audioUrl);
                audio.addEventListener("loadedmetadata", () =>
                    handleLoadedMetadata(index, audio)
                );
                audio.addEventListener("ended", () => handleEnded(index));
                audioRefs.current[index] = audio;
            }
        });

        // Cleanup on unmount
        return () => {
            Object.values(audioRefs.current).forEach((audio) => {
                if (audio) {
                    audio.pause();
                    audio.removeEventListener("loadedmetadata", () => { });
                    audio.removeEventListener("ended", () => { });
                    audio.src = "";
                    audio.load();
                }
            });
        };
    }, [cards, handleLoadedMetadata, handleEnded]);

    // Calculate progress from event position (works for both mouse and touch)
    const calculateProgress = useCallback((index, clientX, progressTrack) => {
        const audio = audioRefs.current[index];
        if (!audio || !audio.duration) return;

        // Batch read: get layout property once to prevent forced reflow
        const rect = progressTrack.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newTime = percentage * audio.duration;

        audio.currentTime = newTime;
        setCurrentTimes((prev) => ({
            ...prev,
            [index]: newTime,
        }));
        setProgress((prev) => ({
            ...prev,
            [index]: percentage * 100,
        }));
    }, []);

    // Handle progress bar click
    const handleProgressClick = (index, e) => {
        // Only handle click if not dragging
        if (isDragging === index) return;
        calculateProgress(index, e.clientX, e.currentTarget);
    };

    // Mouse drag handlers
    const handleMouseDown = (index, e) => {
        e.preventDefault();
        setIsDragging(index);
        dragRef.current = { index, startX: e.clientX };
        calculateProgress(index, e.clientX, e.currentTarget);
    };

    const handleMouseMove = useCallback((e) => {
        if (isDragging === null) return;
        const index = isDragging;
        const progressTrack = document.querySelector(
            `.audio-card[data-card-index="${index}"] .audio-progress-track`
        );
        if (progressTrack) {
            calculateProgress(index, e.clientX, progressTrack);
        }
    }, [isDragging, calculateProgress]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(null);
        dragRef.current = { index: null, startX: 0 };
    }, []);

    // Touch drag handlers
    const handleTouchStart = (index, e) => {
        e.preventDefault();
        const touch = e.touches[0];
        setIsDragging(index);
        dragRef.current = { index, startX: touch.clientX };
        calculateProgress(index, touch.clientX, e.currentTarget);
    };

    const handleTouchMove = useCallback((e) => {
        if (isDragging === null) return;
        e.preventDefault();
        const touch = e.touches[0];
        const index = isDragging;
        const progressTrack = document.querySelector(
            `.audio-card[data-card-index="${index}"] .audio-progress-track`
        );
        if (progressTrack) {
            calculateProgress(index, touch.clientX, progressTrack);
        }
    }, [isDragging, calculateProgress]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(null);
        dragRef.current = { index: null, startX: 0 };
    }, []);

    // Add global event listeners for dragging
    useEffect(() => {
        if (isDragging !== null) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);
            document.addEventListener('touchcancel', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            document.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);


    // Jump audio backward by 10 seconds
    const handlePrev10s = (index) => {
        const audio = audioRefs.current[index];
        if (!audio) return;

        const newTime = Math.max(0, audio.currentTime - 10);
        audio.currentTime = newTime;

        setCurrentTimes((prev) => ({
            ...prev,
            [index]: newTime,
        }));

        if (audio.duration) {
            setProgress((prev) => ({
                ...prev,
                [index]: (newTime / audio.duration) * 100,
            }));
        }
    };

    // Jump audio forward by 10 seconds
    const handleNext10s = (index) => {
        const audio = audioRefs.current[index];
        if (!audio || !audio.duration) return;

        const newTime = Math.min(audio.duration, audio.currentTime + 10);
        audio.currentTime = newTime;

        setCurrentTimes((prev) => ({
            ...prev,
            [index]: newTime,
        }));

        setProgress((prev) => ({
            ...prev,
            [index]: (newTime / audio.duration) * 100,
        }));
    };


    // Early returns after all hooks
    if (!fields) return null;
    if (!cards.length) return null;

    // Get section-level title and subtitle
    const sectionTitle = sanitize(fields.title || "");
    const sectionSubtitle = sanitize(fields.subtitle || "");

    return (
        <section className="audio-grid">
            <div className="canvas">
                {(sectionTitle || sectionSubtitle) && (
                    <div className="audio-grid-header">
                        {sectionSubtitle && <p className="audio-grid-eyebrow">{sectionSubtitle}</p>}
                        {sectionTitle && <h2 className="audio-grid-title">{sectionTitle}</h2>}
                    </div>
                )}
                <div className="audio-grid-inner">
                    {cards.map((card, index) => {
                        const title = sanitize(card.title);
                        const subtitle = sanitize(card.subtitle);
                        const flagUrl = getFlagUrl(card.flag);
                        const audioUrl = getAudioUrl(card.audio_file);
                        const isPlaying = playingIndex === index;
                        const currentTime = currentTimes[index] || 0;
                        const duration = durations[index] || 0;
                        const progressPercent = progress[index] || 0;

                        if (!title && !subtitle) return null;

                        return (
                            <div
                                className="audio-card"
                                key={index}
                                data-card-index={index}
                                data-stagger-index={index}
                            >
                                <div className="left">
                                    {/* FLAG IMAGE ONLY – NO PLAY / PAUSE */}
                                    <div className={`play-btn ${index === 0 ? "first" : ""}`}>
                                        {flagUrl && (
                                            <img
                                                src={flagUrl}
                                                alt={card.flag?.alt || title || "Audio Tour Flag"}
                                                className="flag-image"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                }}
                                            />
                                        )}
                                    </div>


                                    <div className="text">
                                        {/* title + subtitle + share icon */}
                                        <div className="card-head">
                                            <div className="card-text">
                                                {title && <h3>{title}</h3>}
                                                {subtitle && <p>{subtitle}</p>}
                                            </div>

                                            <div className="icons">
                                                {/* Share icon (same as your HTML) */}
                                                <svg
                                                    width="22"
                                                    height="19"
                                                    viewBox="0 0 22 19"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M8.04166 1.75H7C4.05372 1.75 2.58058 1.75 1.66529 2.62868C0.75 3.50736 0.75 4.92157 0.75 7.75V11.75C0.75 14.5784 0.75 15.9926 1.66529 16.8713C2.58058 17.75 4.05372 17.75 7 17.75H11.2066C14.1529 17.75 15.626 17.75 16.5413 16.8713C17.1343 16.302 17.3431 15.5079 17.4166 14.25"
                                                        stroke="#777E91"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M14.8127 4.25V1.10355C14.8127 0.90829 14.9776 0.75 15.181 0.75C15.2786 0.75 15.3723 0.78725 15.4414 0.85355L20.1735 5.39645C20.4094 5.62282 20.5418 5.92986 20.5418 6.25C20.5418 6.57014 20.4094 6.87718 20.1735 7.10355L15.4414 11.6464C15.3723 11.7128 15.2786 11.75 15.181 11.75C14.9776 11.75 14.8127 11.5917 14.8127 11.3964V8.25H11.7617C7.521 8.25 5.9585 11.75 5.9585 11.75V9.25C5.9585 6.48858 8.29035 4.25 11.1668 4.25H14.8127Z"
                                                        stroke="#777E91"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* PLAY LINE + TIME PILL */}
                                        <div className="audio-progress-row">
                                            <div className="icn-flex">
                                                {/* small play icon */}


                                                {/* bar + knob */}
                                                <div
                                                    className={`audio-progress-track ${isDragging === index ? 'dragging' : ''}`}
                                                    onClick={(e) => handleProgressClick(index, e)}
                                                    onMouseDown={(e) => handleMouseDown(index, e)}
                                                    onTouchStart={(e) => handleTouchStart(index, e)}
                                                    style={{
                                                        cursor: audioUrl ? "pointer" : "default",
                                                        touchAction: "none",
                                                        userSelect: "none",
                                                        WebkitUserSelect: "none"
                                                    }}
                                                >
                                                    <div
                                                        className="audio-progress-fill"
                                                        style={{ width: `${progressPercent}%` }}
                                                    />
                                                    <div
                                                        className="audio-progress-knob"
                                                        style={{ left: `${progressPercent}%` }}
                                                    />

                                                </div>

                                            </div>

                                            <div className="audio-prev-next">
                                                <div className="audio-time-pill">
                                                    {formatTime(currentTime)} / {formatTime(duration)}
                                                </div>
                                                {/* <div className="play-next-prev">
                                                    <div className="play-prev">
                                                        <img src={play_prev} alt="play-prev-btn" />
                                                    </div>
                                                    <div className="play-next">
                                                        <img src={play_next} alt="play-next-btn" />
                                                    </div>
                                                </div> */}
                                                {/* <div className="play-next-prev"> */}
                                                <div className={`play-next-prev ${isPlaying ? "show" : ""}`}>
                                                    <div
                                                        className="play-prev"
                                                        onClick={() => handlePrev10s(index)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <img src={play_prev} alt="play-prev-btn" />
                                                    </div>

                                                    <div
                                                        className="play-next"
                                                        onClick={() => handleNext10s(index)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <img src={play_next} alt="play-next-btn" />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </div>
                                <div
                                    className="audio-play-icon"
                                    onClick={() => handlePlayPause(index, audioUrl)}
                                    style={{ cursor: audioUrl ? "pointer" : "default" }}
                                >
                                    {!isPlaying ? (
                                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_3235_16530)">
                                                <path d="M18.7012 10.8C18.7017 11.0037 18.6502 11.2041 18.5518 11.3816C18.4535 11.5592 18.3115 11.7079 18.1398 11.8132L7.49753 18.4234C7.31811 18.5349 7.11261 18.5958 6.90225 18.5998C6.6919 18.6037 6.48431 18.5506 6.30094 18.4459C6.11932 18.3428 5.96802 18.1924 5.86261 18.0103C5.7572 17.8281 5.70148 17.6207 5.70117 17.4094V4.19057C5.70148 3.97929 5.7572 3.77191 5.86261 3.58975C5.96802 3.40759 6.11932 3.25722 6.30094 3.15412C6.48431 3.04938 6.6919 2.99625 6.90225 3.00021C7.11261 3.00416 7.31811 3.06506 7.49753 3.17662L18.1398 9.7868C18.3115 9.89215 18.4535 10.0408 18.5518 10.2184C18.6502 10.3959 18.7017 10.5963 18.7012 10.8Z" fill="white" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_3235_16530">
                                                    <rect width="21.6" height="21.6" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_2774_21105)">
                                                <path d="M15.1875 3.375V14.625C15.1875 14.9234 15.069 15.2095 14.858 15.4205C14.647 15.6315 14.3609 15.75 14.0625 15.75H11.25C10.9516 15.75 10.6655 15.6315 10.4545 15.4205C10.2435 15.2095 10.125 14.9234 10.125 14.625V3.375C10.125 3.07663 10.2435 2.79048 10.4545 2.5795C10.6655 2.36853 10.9516 2.25 11.25 2.25H14.0625C14.3609 2.25 14.647 2.36853 14.858 2.5795C15.069 2.79048 15.1875 3.07663 15.1875 3.375ZM6.75 2.25H3.9375C3.63913 2.25 3.35298 2.36853 3.142 2.5795C2.93103 2.79048 2.8125 3.07663 2.8125 3.375V14.625C2.8125 14.9234 2.93103 15.2095 3.142 15.4205C3.35298 15.6315 3.63913 15.75 3.9375 15.75H6.75C7.04837 15.75 7.33452 15.6315 7.5455 15.4205C7.75647 15.2095 7.875 14.9234 7.875 14.625V3.375C7.875 3.07663 7.75647 2.79048 7.5455 2.5795C7.33452 2.36853 7.04837 2.25 6.75 2.25Z" fill="white" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_2774_21105">
                                                    <rect width="18" height="18" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>)}



                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default AudioGridSection;



